import { showError } from '@calendly/alert';
import { I18n } from '@calendly/i18n';
import { getTimezone } from '@calendly/timezone-tools';
import { Avatar } from '@calendly/ui/components/avatar';
import { IconButton } from '@calendly/ui/components/icon-button';
import { SettingsSliderHorizontalIcon } from '@calendly/ui/components/icons';
import { LabelText, TitleText } from '@calendly/ui/components/typography';
import { colorIconActive, colorIconStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { groupBy, partition } from 'lodash-es';
import { forwardRef, Fragment, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useGetUserQuery } from 'common';
import { Layout } from 'components/layout';
import { FullStoryExclude, FullStoryMask } from 'shared/fullstory_privacy';
import { useHasFormErrors } from '../../../../../../hooks';
import { useMembershipsWithActiveAvailability } from '../../../../hooks';
import { useAvailabilitySidePanel } from '../../availability_side_panel';
import { AvailabilitySummaryMessage } from '../availability_summary_message';
const containerCss = "c3pmwa";
const Container = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(containerCss, props.className)} />);
const listBodyCss = "l1we17q1";
const ListBody = forwardRef((props, ref) => <ul ref={ref} {...props} className={classNames(listBodyCss, props.className)} />);
const listItemCanEditCss = "lomzsf0";
const listItemIsSelectedCss = "lpvepcw";
const listItemCss = "l1e7m32w";
const ListItem = forwardRef((props, ref) => {
  const {
    canEdit,
    isSelected,
    ...restProps
  } = props;
  return <li ref={ref} {...restProps} className={classNames(listItemCss, {
    [listItemCanEditCss]: canEdit,
    [listItemIsSelectedCss]: isSelected
  }, props.className)} />;
});
const listHeaderCss = "lxb2t97";
const ListHeader = forwardRef((props, ref) => <li ref={ref} {...props} className={classNames(listHeaderCss, props.className)} />);
const editButtonCss = "e8dybo1";
const EditButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(editButtonCss, props.className)} />);
const AvailabilityItemLayout = ({
  name,
  avatarUrl,
  avatarLetter,
  summary,
  canEdit,
  openAvailability,
  isSelected,
  itemRef
}) => <ListItem ref={itemRef} canEdit={canEdit} isSelected={isSelected}>
    <Layout align="center" gap="12">
      <FullStoryExclude>
        <Avatar size="x-small" src={avatarUrl} letter={avatarLetter} alt={name} />
      </FullStoryExclude>
      <Layout direction="column" align="start">
        <LabelText size="large"><FullStoryMask>{name}</FullStoryMask></LabelText>
        {summary}
      </Layout>
    </Layout>
    {canEdit && <EditButton size="small" title={I18n.t('publisher.event_type_editor.sections.availability.multi_host.edit_label', {
    name
  })} onClick={openAvailability}>
        <SettingsSliderHorizontalIcon color={isSelected ? colorIconActive : colorIconStandard} />
      </EditButton>}
  </ListItem>;
const AvailabilitySummary = ({
  membershipWithAvailability
}) => {
  const {
    data: currentUser
  } = useGetUserQuery();
  const {
    time_notation: timeNotation
  } = currentUser;
  return <AvailabilitySummaryMessage availableHours={membershipWithAvailability.availableHours} timezone={membershipWithAvailability.timezone} timeNotation={timeNotation} dateOverrides={membershipWithAvailability.dateOverrides} isPending={membershipWithAvailability.is_pending} size="small" color="muted" />;
};
const AvailabilityItem = ({
  membershipWithAvailability,
  isReadOnly
}) => {
  const dispatch = useDispatch();
  const targetRef = useRef(null);
  const {
    formPath,
    memberName,
    avatar_url: avatarUrl,
    avatar_letter: avatarLetter
  } = membershipWithAvailability;
  const hasErrors = useHasFormErrors();
  const {
    availabilitySidePanelProps,
    toggleAvailabilitySidePanel
  } = useAvailabilitySidePanel();
  const isSelected = availabilitySidePanelProps.formPath === formPath;
  const handleItemSelect = useCallback(() => {
    if (hasErrors) {
      dispatch(showError(I18n.t('publisher.event_type_editor.sections.errors.section')));
    } else {
      toggleAvailabilitySidePanel({
        formPath,
        format: 'list',
        checkFormat: false,
        targetRef
      });
    }
  }, [dispatch, formPath, hasErrors, toggleAvailabilitySidePanel]);
  return <AvailabilityItemLayout name={memberName} avatarUrl={avatarUrl} avatarLetter={avatarLetter} summary={<AvailabilitySummary membershipWithAvailability={membershipWithAvailability} />} canEdit={!isReadOnly} openAvailability={handleItemSelect} isSelected={isSelected} itemRef={targetRef} />;
};
export const ActiveHostSummaries = ({
  isReadOnly
}) => {
  const membershipsWithAvailability = useMembershipsWithActiveAvailability();
  const [hosts, pendingHosts] = useMemo(() => partition(membershipsWithAvailability, ({
    is_pending: isPending
  }) => !isPending), [membershipsWithAvailability]);
  const hostsByTimeZone = useMemo(() => groupBy(hosts, 'timezone'), [hosts]);
  const timezones = useMemo(() => Object.keys(hostsByTimeZone), [hostsByTimeZone]);
  if (!hosts.length && !pendingHosts.length) return null;
  return <Container>
      <ListBody>
        {timezones.map(timeZoneNameKey => <Fragment key={timeZoneNameKey}>
              <ListHeader>
                <TitleText size="x-small">
                  {getTimezone(timeZoneNameKey).name}
                </TitleText>
              </ListHeader>
              {hostsByTimeZone[timeZoneNameKey].map(membershipWithAvailability => <AvailabilityItem key={membershipWithAvailability.user_id} membershipWithAvailability={membershipWithAvailability} isReadOnly={isReadOnly} />)}
            </Fragment>)}
        {Array.isArray(pendingHosts) && !!pendingHosts.length && <>
              <ListHeader>
                <TitleText size="x-small">
                  {I18n.t('publisher.event_type_editor.sections.availability.multi_host.pending_users')}
                </TitleText>
              </ListHeader>
              {pendingHosts.map(membershipWithAvailability => <AvailabilityItem key={membershipWithAvailability.organization_invitation_id} membershipWithAvailability={membershipWithAvailability} isReadOnly={isReadOnly} />)}
            </>}
      </ListBody>
    </Container>;
};

require("./active_host_summaries.wyw-in-js.css!=!../../../../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./active_host_summaries.js");